import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function requirementKind(value) {
  const name = String(value || '');
  if (/tax.*refund.*affidavit|affidavit.*tax.*refund/i.test(name)) return 'tax_refund_affidavit';
  if (/\bdso\b|domestic\s+support\s+obligation/i.test(name)) return 'dso_form';
  if (/tax\s*return/i.test(name)) return 'tax_return';
  if (/(?:^|\b)w[\s-]?2s?\b|\b1099s?\b/i.test(name)) return 'w2_1099';
  if (/vehicle\s+registration|official\s+title\s+report|\bmvds\b/i.test(name)) return 'vehicle_registration';
  if (/photo\s*(?:id|identification)|driver'?s?\s+licen[cs]e/i.test(name)) return 'photo_id';
  if (/social\s*security\s*(?:card|document)|\bssn\s*(?:card|document)/i.test(name)) return 'social_security_card';
  if (/deed\s+of\s+trust|trust\s+deed/i.test(name)) return 'deed_of_trust';
  return '';
}

function vehicleDescriptor(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\bvehicle registration\b/g, ' ')
    .replace(/\btitle official title report or mvds\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function trusteeRequirementUploads(checklist, requirementName) {
  const kind = requirementKind(requirementName);
  if (!kind) return [];

  const requestedVehicle = kind === 'vehicle_registration'
    ? vehicleDescriptor(requirementName)
    : '';
  const seen = new Set();
  return (checklist || []).flatMap((item) => {
    if (requirementKind(item?.name) !== kind) return [];
    if (kind === 'vehicle_registration') {
      const checklistVehicle = vehicleDescriptor(item?.name);
      if (requestedVehicle && checklistVehicle && requestedVehicle !== checklistVehicle) return [];
    }
    return (item?.uploads || [])
      .filter((upload) => (
        upload?.id
        && upload.status !== 'rejected'
        && !seen.has(String(upload.id))
      ))
      .map((upload) => {
        seen.add(String(upload.id));
        return {
          id: upload.id,
          filename: upload.filename || item.name || 'document',
          checklist_name: item.name || '',
          download_url: `/uploaded-document/${upload.id}/raw/`,
        };
      });
  });
}

function uniqueDocumentUploads(uploads) {
  const seen = new Set();
  return (uploads || []).filter((upload) => {
    const key = String(upload?.id || upload?.download_url || '');
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function safeDownloadName(caseId, caseNumber) {
  const caseLabel = String(caseNumber || caseId || 'Case')
    .replace(/[^A-Za-z0-9._-]+/g, '_')
    .replace(/^[_.]+|[_.]+$/g, '');
  return `${caseLabel || 'Case'}_Trustee_Documents.pdf`;
}

export function TrusteeDocumentMergePanel({ caseId, caseNumber = '', uploads = [] }) {
  const uniqueUploads = useMemo(() => uniqueDocumentUploads(uploads), [uploads]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const availableIds = new Set(uniqueUploads.map((upload) => String(upload.id)));
    setSelectedIds((current) => current.filter((id) => availableIds.has(String(id))));
  }, [uniqueUploads]);

  if (uniqueUploads.length < 2) return null;

  const toggleUpload = (uploadId) => {
    const key = String(uploadId);
    setSelectedIds((current) => (
      current.includes(key)
        ? current.filter((id) => id !== key)
        : [...current, key]
    ));
  };

  const selectAll = () => {
    if (selectedIds.length === uniqueUploads.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(uniqueUploads.map((upload) => String(upload.id)));
    }
  };

  const mergeDocuments = async () => {
    if (selectedIds.length < 2) return;
    setMerging(true);
    setError('');
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/firm/cases/${caseId}/trustee-documents/merge/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ upload_ids: selectedIds }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.detail || `Could not merge the documents (${response.status}).`);
      }
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = safeDownloadName(caseId, caseNumber);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
    } catch (mergeError) {
      setError(mergeError.message || 'Could not merge the selected documents.');
    } finally {
      setMerging(false);
    }
  };

  return (
    <details className="group mt-4 overflow-hidden rounded-lg border border-indigo-200 bg-indigo-50">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 [&::-webkit-details-marker]:hidden">
        <div className="min-w-0">
          <h5 className="text-sm font-semibold text-indigo-950">Merge trustee documents</h5>
          <p className="mt-0.5 text-xs text-indigo-800">
            {uniqueUploads.length} documents available
            {selectedIds.length > 0 ? ` · ${selectedIds.length} selected` : ''}
          </p>
        </div>
        <ChevronDownIcon className="h-5 w-5 shrink-0 text-indigo-700 transition-transform group-open:rotate-180" />
      </summary>

      <div className="border-t border-indigo-200 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <p className="text-xs leading-5 text-indigo-800">
            Select documents in the order they should appear in the combined PDF.
          </p>
          <button
            type="button"
            onClick={selectAll}
            className="shrink-0 rounded-md border border-indigo-300 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
          >
            {selectedIds.length === uniqueUploads.length ? 'Clear all' : 'Select all'}
          </button>
        </div>
        <div className="mt-3 space-y-2">
          {uniqueUploads.map((upload) => {
            const selectedPosition = selectedIds.indexOf(String(upload.id));
            return (
              <label
                key={upload.id}
                className="flex cursor-pointer items-start gap-2 rounded-md border border-indigo-100 bg-white px-3 py-2 text-xs text-slate-700"
              >
                <input
                  type="checkbox"
                  checked={selectedPosition >= 0}
                  onChange={() => toggleUpload(upload.id)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                {selectedPosition >= 0 && (
                  <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-indigo-100 px-1 text-[11px] font-bold text-indigo-800">
                    {selectedPosition + 1}
                  </span>
                )}
                <span className="min-w-0">
                  <span className="block break-words font-semibold text-slate-900">{upload.filename}</span>
                  {upload.checklist_name && (
                    <span className="mt-0.5 block text-slate-500">{upload.checklist_name}</span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
        <button
          type="button"
          onClick={mergeDocuments}
          disabled={selectedIds.length < 2 || merging}
          className="mt-3 inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          {merging ? 'Merging…' : `Merge and download (${selectedIds.length})`}
        </button>
        {error && (
          <p className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-red-700">
            <ExclamationTriangleIcon className="h-4 w-4" /> {error}
          </p>
        )}
      </div>
    </details>
  );
}

export default function TrusteeRequirementDocumentLinks({ uploads = [] }) {
  const [openingId, setOpeningId] = useState(null);
  const [error, setError] = useState('');
  const uniqueUploads = useMemo(() => {
    return uniqueDocumentUploads(uploads);
  }, [uploads]);

  if (!uniqueUploads.length) return null;

  const openUpload = async (upload) => {
    setOpeningId(upload.id);
    setError('');
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}${upload.download_url}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(`Could not open the document (${response.status}).`);
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
    } catch (openError) {
      setError(openError.message || 'Could not open the document.');
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <span className="ml-2 inline-flex flex-wrap items-center gap-1.5 align-middle">
      {uniqueUploads.map((upload) => (
        <button
          key={upload.id}
          type="button"
          onClick={() => openUpload(upload)}
          disabled={openingId === upload.id}
          className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-white px-2 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-60"
          title={`${upload.checklist_name ? `${upload.checklist_name}: ` : ''}${upload.filename}`}
        >
          <ArrowDownTrayIcon className="h-3.5 w-3.5" />
          {openingId === upload.id ? 'Opening…' : upload.filename}
        </button>
      ))}
      {error && (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700">
          <ExclamationTriangleIcon className="h-3.5 w-3.5" /> {error}
        </span>
      )}
    </span>
  );
}
